/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { Loader } from "./components/Loader";
import { useAuth } from "./contexts/AuthContext";
import { ENDPOINTS } from "./endpoints";
import { temPermissao, garantirModuloPreventivas } from "./utils/permissoes";
import { ordenarPorTexto } from "./utils/ordenar";

type Usuario = {
  id: number;
  nome: string;
  login: string;
  senha: string;
  grupo_id: number;
  grupo_nome: string;
  departamento_id?: number;
  departamento_descricao?: string;
  ativo: boolean;
  whatsapp?: string;
  validador?: number;
  descricao?: string;
  permissoes?: string | object;
};

const gruposFixos = [
  "ADMINISTRADOR",
  "OS GESTOR",
  "LIDER",
  "EXECUTOR",
  "FUNCIONARIO",
  "BOT",
  "PRECEPTOR",
  "COORDENADOR"
];

function Toast({ open, message, type = 'error', onClose }: { open: boolean, message: string, type?: 'error' | 'success', onClose: () => void }) {
  if (!open) return null;
  return (
    <>
      <style>{`
        .toast-anim {
          animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes zoomInFade {
          0% {
            opacity: 0;
            transform: scale(0.7);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <div className={`toast-anim fixed top-6 left-1/2 z-[9999] -translate-x-1/2 px-6 py-3 rounded shadow-lg text-white font-semibold transition-all
        ${type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}
        style={{ minWidth: 220, maxWidth: 400, textAlign: 'center' }}
      >
        {message}
      </div>
    </>
  );
}

export default function UsuariosCRUD() {
  const { loginUnidade, permissoes, loginGrupoNome } = useAuth();
  const [dados, setDados] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<{ id: number; descricao: string }[]>([]);
  const [filtro, setFiltro] = useState({ descricao: "", id: "", login: "", nome: "", whatsapp: "", departamento: "" });
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState<Partial<Usuario & { senha?: string }>>({ grupo_id: 1, descricao: "ADMINISTRADOR", validador: 0, senha: '' });
  const [msg, setMsg] = useState<string | null>(null);
  const [toast, setToast] = useState<{ open: boolean, message: string, type?: 'error' | 'success' }>({ open: false, message: '', type: 'error' });
  const [confirmaExcluir, setConfirmaExcluir] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const [sort, setSort] = useState<{ coluna: keyof Usuario; direcao: 'asc' | 'desc' }>({ coluna: 'id', direcao: 'asc' });
  const [modalPermissoes, setModalPermissoes] = useState(false);
  const [permissoesMemorizadas, setPermissoesMemorizadas] = useState<any>(null);
  const [permissoesEditaveis, setPermissoesEditaveis] = useState<any>(null);

  // Verificações de permissão
  const podeAcessar = temPermissao(permissoes, "usuarios", "acessar_modulo");
  const podeNovo = temPermissao(permissoes, "usuarios", "novo_usuario");
  const podeAlterar = temPermissao(permissoes, "usuarios", "alterar_usuario");
  const podeExcluir = temPermissao(permissoes, "usuarios", "excluir_usuario");
  
  // Verificar se pode visualizar/editar permissões (apenas ADMINISTRADOR ou OS GESTOR)
  const podeGerenciarPermissoes = loginGrupoNome === "ADMINISTRADOR" || loginGrupoNome === "OS GESTOR";

  useEffect(() => {
    if (!podeAcessar) return;
    async function fetchUsuarios() {
      if (!loginUnidade) return;
      setCarregando(true);
      try {
        const response = await fetch(ENDPOINTS.USUARIO_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
        });
        const data = await response.json();
        setDados(Array.isArray(data) ? data : []);
      } catch (err) {
        setDados([]);
      }
      setCarregando(false);
    }

    async function fetchDepartamentos() {
      if (!loginUnidade) return;
      try {
        const response = await fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
        });
        const data = await response.json();
        console.log('Departamentos carregados:', data);
        setDepartamentos(ordenarPorTexto(Array.isArray(data) ? data : [], "descricao"));
      } catch (err) {
        console.error('Erro ao carregar departamentos:', err);
        setDepartamentos([]);
      }
    }

    fetchUsuarios();
    fetchDepartamentos();
  }, [loginUnidade, podeAcessar]);

  if (!podeAcessar) {
    return <div className="p-8 text-center text-red-600 font-bold text-xl">Você não tem permissão para acessar este módulo.</div>;
  }

  // Função para mapear departamento_id para departamento_descricao
  const mapearDepartamentos = (usuarios: Usuario[], departamentosList: { id: number; descricao: string }[]) => {
    return usuarios.map(usuario => ({
      ...usuario,
      departamento_descricao: usuario.departamento_id 
        ? departamentosList.find(dept => dept.id === usuario.departamento_id)?.descricao || `Dept. ${usuario.departamento_id}`
        : undefined
    }));
  };

  // Dados com departamentos mapeados
  const dadosComDepartamentos = mapearDepartamentos(dados, departamentos);

  // Filtro aplicado + ordenação
  const dadosFiltrados = [...dadosComDepartamentos].filter(u =>
    (filtro.descricao === "" || (u.descricao?.toLowerCase() || "").includes(filtro.descricao.toLowerCase())) &&
    (filtro.id === "" || u.id.toString().includes(filtro.id)) &&
    (filtro.login === "" || u.login.toLowerCase().includes(filtro.login.toLowerCase())) &&
    (filtro.nome === "" || u.nome.toLowerCase().includes(filtro.nome.toLowerCase())) &&
    (filtro.whatsapp === "" || (u.whatsapp?.toLowerCase() || "").includes(filtro.whatsapp.toLowerCase())) &&
    (filtro.departamento === "" || (u.departamento_descricao?.toLowerCase() || "").includes(filtro.departamento.toLowerCase()))
  ).sort((a, b) => {
    if (sort.coluna === "id") {
      return sort.direcao === "asc" ? a.id - b.id : b.id - a.id;
    }
    if (sort.coluna === "nome") {
      return sort.direcao === "asc" ? a.nome.localeCompare(b.nome) : b.nome.localeCompare(a.nome);
    }
    if (sort.coluna === "login") {
      return sort.direcao === "asc" ? a.login.localeCompare(b.login) : b.login.localeCompare(a.login);
    }
    if (sort.coluna === "grupo_nome") {
      return sort.direcao === "asc" ? a.grupo_nome.localeCompare(b.grupo_nome) : b.grupo_nome.localeCompare(a.grupo_nome);
    }
    return 0;
  });

  function handleEdit(usuario: Usuario) {
    setEditando(usuario);
    setForm(usuario);
    setNovo(false);
    // Buscar as permissões do usuário sendo editado
    buscarPermissoesUsuario(usuario.id);
  }

  // Função para buscar as permissões de um usuário específico
  async function buscarPermissoesUsuario(usuarioId: number) {
    console.log('Buscando permissões para usuário ID:', usuarioId);
    
    try {
      // Primeiro, tentar encontrar na lista atual em memória
      const usuarioLocal = dados.find(u => u.id === usuarioId);
      console.log('Usuário local encontrado:', usuarioLocal);
      
      if (usuarioLocal && (usuarioLocal as any).permissoes) {
        console.log('Permissões encontradas localmente:', (usuarioLocal as any).permissoes);
        const permissoesUsuario = typeof (usuarioLocal as any).permissoes === 'string' 
          ? JSON.parse((usuarioLocal as any).permissoes) 
          : (usuarioLocal as any).permissoes;
        setPermissoesMemorizadas(permissoesUsuario);
        return;
      }
      
      // Se não encontrar localmente, buscar no backend
      console.log('Buscando permissões no backend...');
      const response = await fetch(ENDPOINTS.USUARIO_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          unidade: (loginUnidade || '').toLowerCase()
        })
      });
      const data = await response.json();
      console.log('Resposta do backend:', data);
      
      // Procurar o usuário específico na lista retornada
      const usuarioEncontrado = Array.isArray(data) 
        ? data.find((u: any) => u.id === usuarioId)
        : null;
      
      console.log('Usuário encontrado no backend:', usuarioEncontrado);
        
      if (usuarioEncontrado && usuarioEncontrado.permissoes) {
        console.log('Permissões do usuário encontradas:', usuarioEncontrado.permissoes);
        // Se vier como string, fazer parse; se já for objeto, usar direto
        const permissoesUsuario = typeof usuarioEncontrado.permissoes === 'string' 
          ? JSON.parse(usuarioEncontrado.permissoes) 
          : usuarioEncontrado.permissoes;
        setPermissoesMemorizadas(permissoesUsuario);
      } else {
        console.log('Permissões não encontradas, usando padrão do contexto');
        // Se não tiver permissões específicas, usar as padrão do contexto
        setPermissoesMemorizadas(permissoes);
      }
    } catch (err) {
      console.error('Erro ao buscar permissões do usuário:', err);
      // Em caso de erro, usar as permissões padrão do contexto
      setPermissoesMemorizadas(permissoes);
    }
  }

  function handleDelete(usuario: Usuario) {
    setConfirmaExcluir(usuario);
  }
  async function handleDeleteConfirm() {
    if (confirmaExcluir) {
      setCarregando(true);
      try {
        const response = await fetch(ENDPOINTS.USUARIO_DELETE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            unidade: (loginUnidade || '').toLowerCase(),
            id: confirmaExcluir.id
          })
        });
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.success) {
          setMsg("Usuário removido!");
          setConfirmaExcluir(null);
          // Recarregar lista
          try {
            const response = await fetch(ENDPOINTS.USUARIO_LIST, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
            });
            const data = await response.json();
            setDados(Array.isArray(data) ? data : []);
          } catch (err) {}
        } else {
          setMsg("Erro ao remover usuário!");
        }
      } catch (err) {
        setMsg("Erro de conexão ao remover!");
      }
      setCarregando(false);
      setTimeout(() => setMsg(null), 2000);
    }
  }
  async function handleSave() {
    if (!form.grupo_id || !form.login || !form.nome || !form.whatsapp || (novo && !form.senha)) {
      setToast({ open: true, message: "Preencha todos os campos obrigatórios!", type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      return;
    }
    setCarregando(true);
    // Determinar as permissões a serem enviadas
    const permissoesParaEnviar = garantirModuloPreventivas(permissoesMemorizadas || permissoes || {});
    if (novo) {
      try {
        const payload = {
          unidade: (loginUnidade || '').toLowerCase(),
          grupo_id: form.grupo_id,
          login: form.login,
          nome: form.nome,
          senha: form.senha,
          whatsapp: form.whatsapp,
          validador: form.validador || 0,
          departamento_id: form.departamento_id !== undefined ? form.departamento_id : null,
          permissoes: JSON.stringify(permissoesParaEnviar)
        };
        console.log('Payload USUARIO_CREATE:', payload);
        const response = await fetch(ENDPOINTS.USUARIO_CREATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        console.log('Resposta USUARIO_CREATE:', data);
        if (Array.isArray(data) && data[0]?.success) {
          setMsg("Usuário adicionado!");
          setNovo(false);
          setEditando(null);
          setForm({ grupo_id: 1, descricao: "ADMINISTRADOR", validador: 0, senha: '' });
          // Recarregar lista
          try {
            const response = await fetch(ENDPOINTS.USUARIO_LIST, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
            });
            const data = await response.json();
            setDados(Array.isArray(data) ? data : []);
          } catch (err) {}
        } else {
          setMsg("Erro ao adicionar usuário!");
        }
      } catch (err) {
        setMsg("Erro de conexão ao adicionar!");
      }
      setCarregando(false);
      setTimeout(() => setMsg(null), 2000);
      return;
    }
    if (editando) {
      try {
        const payload: any = {
          unidade: (loginUnidade || '').toLowerCase(),
          id: editando.id,
          grupo_id: form.grupo_id,
          login: form.login,
          nome: form.nome,
          whatsapp: form.whatsapp,
          validador: form.validador || 0,
          departamento_id: form.departamento_id !== undefined ? form.departamento_id : null,
          permissoes: JSON.stringify(permissoesParaEnviar)
        };
        if (form.senha && form.senha.trim() !== '') {
          payload.senha = form.senha;
        }
        console.log('Payload USUARIO_UPDATE:', payload);
        const response = await fetch(ENDPOINTS.USUARIO_UPDATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        console.log('Resposta USUARIO_UPDATE:', data);
        if (Array.isArray(data) && data[0]?.success) {
          setMsg("Usuário atualizado!");
          setEditando(null);
          setNovo(false);
          setForm({ grupo_id: 1, descricao: "ADMINISTRADOR", validador: 0, senha: '' });
          // Recarregar lista
          try {
            const response = await fetch(ENDPOINTS.USUARIO_LIST, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
            });
            const data = await response.json();
            setDados(Array.isArray(data) ? data : []);
          } catch (err) {}
        } else {
          setMsg("Erro ao atualizar usuário!");
        }
      } catch (err) {
        setMsg("Erro de conexão ao atualizar!");
      }
      setCarregando(false);
      setTimeout(() => setMsg(null), 2000);
      return;
    }
    setEditando(null);
    setNovo(false);
    setForm({ grupo_id: 1, descricao: "ADMINISTRADOR", validador: 0, senha: '' });
    setCarregando(false);
    setTimeout(() => setMsg(null), 2000);
  }
  function handleCancel() {
    setEditando(null);
    setNovo(false);
    setForm({ grupo_id: 1, descricao: "ADMINISTRADOR", validador: 0, senha: '' });
  }
  function handleAdd() {
    setNovo(true);
    setEditando(null);
    setForm({ grupo_id: 1, descricao: "ADMINISTRADOR", validador: 0, senha: '' });
    // Para novo usuário, usar as permissões padrão do contexto logado
    setPermissoesMemorizadas(permissoes);
  }

  // Campos obrigatórios para validação
  const obrigatorios = [
    { nome: 'grupo_id', label: 'Grupo' },
    { nome: 'login', label: 'Login' },
    { nome: 'nome', label: 'Nome' },
    { nome: 'whatsapp', label: 'WhatsApp' },
  ];

  // Função para abrir o modal de permissões com uma cópia editável
  function abrirModalPermissoes() {
    const base = permissoesMemorizadas || permissoes;
    setPermissoesEditaveis(garantirModuloPreventivas(JSON.parse(JSON.stringify(base || {}))));
    setModalPermissoes(true);
  }

  // Função para lidar com mudança de checkbox
  function handlePermissaoChange(modulo: string, acao: string) {
    setPermissoesEditaveis((prev: any) => {
      const novo = { ...prev };
      novo[modulo] = { ...novo[modulo], [acao]: novo[modulo][acao] === 'sim' ? 'nao' : 'sim' };
      return novo;
    });
  }

  // Função para renderizar as permissões em formato de lista
  function renderPermissoes(permissoesObj: any) {
    if (!permissoesObj) return <div>Nenhuma permissão encontrada.</div>;
    return (
      <div className="space-y-4">
        {Object.entries(permissoesObj).map(([modulo, permissoesModulo]: any) => (
          <div key={modulo}>
            <div className="font-bold mb-1 capitalize text-black">{modulo.replace(/_/g, ' ')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 ml-2">
              {Object.entries(permissoesModulo).map(([acao, valor]: any) => (
                <label key={acao} className="flex items-center gap-2 text-sm text-black">
                  <input
                    type="checkbox"
                    checked={valor === 'sim'}
                    onChange={() => handlePermissaoChange(modulo, acao)}
                  />
                  <span>{acao.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full mt-4 bg-white rounded-lg shadow p-2 sm:p-6">
      <Toast open={toast.open} message={toast.message} type={toast.type} onClose={() => setToast(t => ({ ...t, open: false }))} />
      {(carregando) && <Loader />}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-xl sm:text-2xl font-bold text-black">Usuários</h2>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setCarregando(true);
              Promise.all([
                fetch(ENDPOINTS.USUARIO_LIST, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
                }).then(res => res.json()).then(data => setDados(Array.isArray(data) ? data : [])),
                fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ unidade: loginUnidade.toLowerCase() })
                }).then(res => res.json()).then(data => {
                  console.log('Departamentos atualizados:', data);
                  setDepartamentos(ordenarPorTexto(Array.isArray(data) ? data : [], "descricao"));
                })
              ]).finally(() => setCarregando(false));
            }}
            className="bg-green-600 text-white px-4 py-2 rounded font-semibold hover:bg-green-700 flex items-center gap-2"
            title="Atualizar dados"
          >
            <i className="fa fa-sync-alt"></i>
            Atualizar
          </button>
          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-700"
            disabled={!podeNovo}
            style={{ opacity: podeNovo ? 1 : 0.5, pointerEvents: podeNovo ? 'auto' : 'none' }}
          >
            Novo Usuário
          </button>
        </div>
      </div>
      {msg && <div className="mb-2 p-2 bg-green-100 text-green-800 rounded text-center">{msg}</div>}
      {/* Cabeçalho e filtros - FORA do scroll */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-1 text-xs sm:text-base">
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-100">
              <th className="p-1 sm:p-2 text-left cursor-pointer select-none text-black" onClick={() => setSort(s => ({ coluna: 'descricao', direcao: s.coluna === 'descricao' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                Grupo
                {sort.coluna === 'descricao' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left cursor-pointer select-none text-black" onClick={() => setSort(s => ({ coluna: 'id', direcao: s.coluna === 'id' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                Id
                {sort.coluna === 'id' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left cursor-pointer select-none text-black" onClick={() => setSort(s => ({ coluna: 'login', direcao: s.coluna === 'login' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                Login
                {sort.coluna === 'login' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left cursor-pointer select-none text-black" onClick={() => setSort(s => ({ coluna: 'nome', direcao: s.coluna === 'nome' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                Nome
                {sort.coluna === 'nome' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left cursor-pointer select-none text-black" onClick={() => setSort(s => ({ coluna: 'whatsapp', direcao: s.coluna === 'whatsapp' && s.direcao === 'asc' ? 'desc' : 'asc' }))}>
                WhatsApp
                {sort.coluna === 'whatsapp' && (
                  <span className="ml-1 align-middle">{sort.direcao === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="p-1 sm:p-2 text-left text-black">
                Departamento
              </th>
              <th className="p-1 sm:p-2 text-center text-black">Ações</th>
            </tr>
            <tr className="bg-gray-50">
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.descricao} onChange={e => setFiltro(f => ({ ...f, descricao: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.id} onChange={e => setFiltro(f => ({ ...f, id: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.login} onChange={e => setFiltro(f => ({ ...f, login: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.nome} onChange={e => setFiltro(f => ({ ...f, nome: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.whatsapp} onChange={e => setFiltro(f => ({ ...f, whatsapp: e.target.value }))} placeholder="Filtrar..." /></th>
              <th className="p-1"><input className="w-full rounded px-1 py-1 text-xs sm:text-sm bg-[#fff9c4] text-black" value={filtro.departamento} onChange={e => setFiltro(f => ({ ...f, departamento: e.target.value }))} placeholder="Filtrar..." /></th>
              <th></th>
            </tr>
          </thead>
        </table>
      </div>
      {/* Corpo da tabela COM SCROLL */}
      <div className="overflow-y-auto max-h-[60vh]">
        <table className="min-w-full border-separate border-spacing-y-1 text-xs sm:text-base">
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <tbody>
            {dadosFiltrados.map((usuario, idx) => (
              <tr
                key={usuario.id}
                className={`cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'} ${editando?.id === usuario.id ? 'bg-teal-200' : ''} hover:bg-teal-200`}
                onDoubleClick={() => podeAlterar && handleEdit(usuario)}
              >
                <td className="p-2 font-semibold text-black">{usuario.descricao}</td>
                <td className="p-2 text-black">{usuario.id}</td>
                <td className="p-2 text-black">{usuario.login}</td>
                <td className="p-2 text-black">{usuario.nome}</td>
                <td className="p-2 text-black">{usuario.whatsapp || ''}</td>
                <td className="p-2 text-black">{usuario.departamento_descricao || '-'}</td>
                <td className="p-2 flex gap-2 justify-center">
                  <button
                    onClick={() => podeAlterar && handleEdit(usuario)}
                    className="p-2 rounded bg-blue-600 hover:bg-blue-700 flex items-center justify-center"
                    disabled={!podeAlterar}
                    style={{ opacity: podeAlterar ? 1 : 0.5, pointerEvents: podeAlterar ? 'auto' : 'none' }}
                    title="Editar"
                  >
                    <i className="fa fa-pen text-white"></i>
                  </button>
                  <button
                    onClick={() => podeExcluir && handleDelete(usuario)}
                    className="p-2 rounded bg-red-600 hover:bg-red-700 flex items-center justify-center"
                    disabled={!podeExcluir}
                    style={{ opacity: podeExcluir ? 1 : 0.5, pointerEvents: podeExcluir ? 'auto' : 'none' }}
                    title="Excluir"
                  >
                    <i className="fa fa-trash text-white"></i>
                  </button>
                </td>
              </tr>
            ))}
            {dadosFiltrados.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-gray-400 py-8">Nenhum usuário encontrado.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {/* Modal de inclusão/edição */}
      {(novo || editando) && (
        <>
          {(carregando) && <Loader />}
          <style>{`
            .modal-anim {
              animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            }
            @keyframes zoomInFade {
              0% {
                opacity: 0;
                transform: scale(0.7);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
          <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
            <div className="modal-anim bg-white rounded-lg shadow-2xl p-4 sm:p-8 max-w-[95vw] sm:max-w-[700px] w-full">
              <h3 className="text-xl font-bold mb-4 text-black">
                {novo ? "Novo Usuário" : "Editar Usuário"}
              </h3>
              <div className="w-full flex flex-col md:flex-row gap-4">
                <div className="flex-1 flex flex-col gap-3">
                  <label className="text-sm font-semibold text-black">
                    Grupo <span style={{color: 'red'}}>*</span>
                  </label>
                  <select
                    className={`w-full rounded px-2 py-2 border text-black ${(!form.grupo_id && toast.open && toast.message.includes('obrigatório')) ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.grupo_id || 1}
                    onChange={e => {
                      const grupo_id = Number(e.target.value);
                      const grupo = gruposFixos[grupo_id - 1] || '';
                      setForm(f => ({ ...f, grupo_id, descricao: grupo }));
                    }}
                  >
                    {gruposFixos.map((nome, idx) => (
                      <option key={idx + 1} value={idx + 1}>{nome}</option>
                    ))}
                  </select>
                  {!form.grupo_id && toast.open && toast.message.includes('obrigatório') && (
                    <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                  )}
                  <label className="text-sm font-semibold text-black">ID</label>
                  <input className="w-full rounded px-2 py-1 border border-gray-300 bg-gray-100 text-black" value={editando ? editando.id : "(auto)"} readOnly />
                  <label className="text-sm font-semibold text-black">
                    Login <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    className={`w-full rounded px-2 py-1 border text-black ${(!form.login && toast.open && toast.message.includes('obrigatório')) ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.login || ''}
                    onChange={e => setForm(f => ({ ...f, login: e.target.value }))}
                    placeholder="Digite o login"
                  />
                  {!form.login && toast.open && toast.message.includes('obrigatório') && (
                    <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                  )}
                  <label className="text-sm font-semibold text-black">
                    Nome <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    className={`w-full rounded px-2 py-1 border text-black ${(!form.nome && toast.open && toast.message.includes('obrigatório')) ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.nome || ''}
                    onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                    placeholder="Digite o nome"
                  />
                  {!form.nome && toast.open && toast.message.includes('obrigatório') && (
                    <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                  )}
                  <label className="text-sm font-semibold text-black">
                    WhatsApp <span style={{color: 'red'}}>*</span>
                  </label>
                  <input
                    className={`w-full rounded px-2 py-1 border text-black ${(!form.whatsapp && toast.open && toast.message.includes('obrigatório')) ? 'border-red-500' : 'border-gray-300'}`}
                    value={form.whatsapp || ''}
                    onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
                    placeholder="Digite o WhatsApp"
                  />
                  {!form.whatsapp && toast.open && toast.message.includes('obrigatório') && (
                    <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                  )}
                  <label className="text-sm font-semibold text-black">
                    Departamento
                  </label>
                  <select
                    className="w-full rounded px-2 py-2 border border-gray-300 text-black"
                    value={form.departamento_id || ''}
                    onChange={e => {
                      console.log('Departamento selecionado:', e.target.value);
                      if (e.target.value === '0') {
                        setForm(f => ({ ...f, departamento_id: 0 }));
                      } else if (e.target.value === '') {
                        setForm(f => ({ ...f, departamento_id: undefined }));
                      } else {
                        setForm(f => ({ ...f, departamento_id: Number(e.target.value) }));
                      }
                    }}
                  >
                    <option value="">Selecione um departamento</option>
                    <option value="0">Sem departamento (pode ver todas as OSs)</option>
                    {departamentos.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.descricao}</option>
                    ))}
                  </select>
                  {/* Debug: Mostrar quantos departamentos foram carregados */}
                  <small className="text-gray-500">
                    {departamentos.length > 0 ? `${departamentos.length} departamentos carregados` : 'Nenhum departamento carregado'}
                  </small>
                  {novo && (
                    <>
                      <label className="text-sm font-semibold text-black">
                        Senha <span style={{color: 'red'}}>*</span>
                      </label>
                      <input
                        type={senhaVisivel ? 'text' : 'password'}
                        className={`w-full rounded px-2 py-1 border text-black ${(!form.senha && toast.open && toast.message.includes('obrigatório')) ? 'border-red-500' : 'border-gray-300'}`}
                        value={form.senha || ''}
                        onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                        placeholder="Digite a senha"
                      />
                      {!form.senha && toast.open && toast.message.includes('obrigatório') && (
                        <span className="text-red-500 text-xs mt-1">Campo obrigatório</span>
                      )}
                    </>
                  )}
                  <label className="text-sm font-semibold text-black">Senha</label>
                  <div className="flex items-center gap-2">
                    <input type={senhaVisivel ? "text" : "password"} className="w-full rounded px-2 py-1 border border-gray-300 text-black" value={form.senha || ''} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} placeholder="Digite a senha" />
                    <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setSenhaVisivel(v => !v)} title={senhaVisivel ? "Ocultar senha" : "Mostrar senha"}><i className={`fa ${senhaVisivel ? "fa-eye-slash" : "fa-eye"}`}></i></button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Dica: Use letras maiúsculas, minúsculas, números e símbolos para uma senha forte.</div>
                  <label className="flex items-center gap-2 mt-2 text-black">
                    <input type="checkbox" checked={!!form.validador} onChange={e => setForm(f => ({ ...f, validador: e.target.checked ? 1 : 0 }))} />
                    <span>Este usuário é um <b>validador</b> das ordens de serviço</span>
                  </label>
                  {/* Botão Permissões */}
                  {podeGerenciarPermissoes && (
                    <button
                      type="button"
                      className="bg-gray-700 text-white px-4 py-2 rounded font-semibold hover:bg-gray-800 w-fit mt-2"
                      onClick={abrirModalPermissoes}
                    >
                      Permissões
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-4 mt-6 justify-center">
                <button onClick={handleSave} className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700">Salvar</button>
                <button onClick={handleCancel} className="bg-red-500 text-white px-6 py-2 rounded font-semibold hover:bg-red-600">Cancelar</button>
              </div>
            </div>
          </div>
          {/* Modal de Permissões */}
          {modalPermissoes && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-[9999]">
              <div className="modal-anim bg-white rounded-lg shadow-2xl p-6 max-w-[95vw] sm:max-w-[500px] w-full">
                <h3 className="text-lg font-bold mb-4 text-black">Permissões do Usuário</h3>
                <div className="max-h-[50vh] overflow-y-auto mb-6">
                  {renderPermissoes(permissoesEditaveis)}
                </div>
                <div className="flex gap-4 justify-center mt-4">
                  <button
                    className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700"
                    onClick={() => { setPermissoesMemorizadas(permissoesEditaveis); setModalPermissoes(false); }}
                  >
                    Gravar
                  </button>
                  <button
                    className="bg-gray-500 text-white px-6 py-2 rounded font-semibold hover:bg-gray-600"
                    onClick={() => setModalPermissoes(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {/* Modal de confirmação de exclusão */}
      {confirmaExcluir && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="modal-anim bg-white rounded-lg shadow-2xl p-8 max-w-[95vw] w-full max-w-[400px] flex flex-col items-center">
            <style>{`
              .modal-anim {
                animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
              }
              @keyframes zoomInFade {
                0% {
                  opacity: 0;
                  transform: scale(0.7);
                }
                100% {
                  opacity: 1;
                  transform: scale(1);
                }
              }
            `}</style>
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mb-4">
              <circle cx="40" cy="40" r="38" stroke="#f87171" strokeWidth="4" fill="#fff" />
              <path d="M28 28L52 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
              <path d="M52 28L28 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
            </svg>
            <h3 className="text-xl font-bold mb-2">Confirma exclusão?</h3>
            <p className="text-gray-600 mb-2 text-center">Deseja realmente excluir o usuário <b>{confirmaExcluir.nome}</b>?</p>
            <div className="flex gap-4 mt-6 justify-center">
              <button onClick={handleDeleteConfirm} className="bg-red-600 text-white px-6 py-2 rounded font-semibold hover:bg-red-700">Excluir</button>
              <button onClick={() => setConfirmaExcluir(null)} className="bg-gray-500 text-white px-6 py-2 rounded font-semibold hover:bg-gray-600">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 